import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TableauAuthoringViz } from '@tableau/embedding-api';
import { useSelector } from 'react-redux';
import { AppConfig } from 'AppConfig';
import { fetchData } from 'features/user/userActions';
import { store } from 'store/store';
import getUrlTicket from 'utils/getUrlTicket';
import routePaths from 'routes/routePaths';

const COHORT_BUILD_SHEET = 'Build';
const COHORT_AUTHORING_PATH = `authoring/CohortBuilder/${COHORT_BUILD_SHEET}`;
const COHORT_PHI_AUTHORING_PATH = `authoring/CohortBuilderPHI/${COHORT_BUILD_SHEET}`;

const TENANT_CD_PATTERN = /^[A-Za-z0-9._-]+$/;
const REPT_SOURCE_PATTERN = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;

const normalizeTenantCd = (value) => {
  if (typeof value !== 'string') return '';

  const normalizedValue = value.trim();
  return TENANT_CD_PATTERN.test(normalizedValue) ? normalizedValue : '';
};

const normalizeReptSource = (value) => {
  if (typeof value !== 'string') return '';

  const normalizedValue = value.trim();
  return REPT_SOURCE_PATTERN.test(normalizedValue) ? normalizedValue : '';
};

const getReturnLocation = (returnLocation) => {
  const pathname = typeof returnLocation?.pathname === 'string' ? returnLocation.pathname : '';
  const isFrontOfficeLocation = pathname === routePaths.frontOffice
    || pathname.startsWith(`${routePaths.frontOffice}/`);
  if (!isFrontOfficeLocation) {
    return {
      to: routePaths.frontOffice,
      state: {},
    };
  }

  return {
    to: {
      pathname,
      search: typeof returnLocation.search === 'string' ? returnLocation.search : '',
      hash: typeof returnLocation.hash === 'string' ? returnLocation.hash : '',
    },
    state: returnLocation.state && typeof returnLocation.state === 'object'
      ? returnLocation.state
      : {},
  };
};

const CohortBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const vizRef = useRef(null);
  const containerRef = useRef(null);
  const hasRequestedUserContextRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentTenantCd, tableauServiceUrl, defaultUserState, enigmaAccessTenantCds, companies } = useSelector((state) => state.user);
  const tenantCdFromQuery = normalizeTenantCd(searchParams.get('tenantCd'));
  const tenantCdFromRoute = normalizeTenantCd(location.state?.tenantCd);
  const tenantCd = tenantCdFromQuery || tenantCdFromRoute || normalizeTenantCd(currentTenantCd);
  const tenant = Array.isArray(companies)
    ? companies.find((company) => company?.code === tenantCd)
    : null;
  const isPhiCohortBuilderEnabled = tenant?.phiCohortBuilderEnabled === true;
  const hasTenantEnigmaAccess = Array.isArray(enigmaAccessTenantCds)
    && enigmaAccessTenantCds.includes(tenantCd);
  const openedFromGlobalFilters = Boolean(location.state?.openedFromGlobalFilters);
  const cohortBuilderFlowId = typeof location.state?.cohortBuilderFlowId === 'string' ? location.state.cohortBuilderFlowId : null;
  const returnLocation = location.state?.returnLocation;
  // When editing an existing cohort, load its workbook directly instead of the blank Build view.
  const editCohortReptSource = normalizeReptSource(location.state?.editCohort?.reptSource);
  const editCohortDisplayText = typeof location.state?.editCohort?.displayText === 'string' ? location.state.editCohort.displayText : null;
  const defaultCohortAuthoringPath = isPhiCohortBuilderEnabled && hasTenantEnigmaAccess
    ? COHORT_PHI_AUTHORING_PATH
    : COHORT_AUTHORING_PATH;
  // Saved cohorts are copies of the builder workbook; always open them on the Build sheet
  // rather than whichever view reptSource points to.
  const editCohortWorkbook = editCohortReptSource ? editCohortReptSource.split('/')[0] : '';
  const cohortAuthoringPath = editCohortWorkbook
    ? `authoring/${editCohortWorkbook}/${COHORT_BUILD_SHEET}`
    : defaultCohortAuthoringPath;

  const returnToFrontOffice = useCallback((cohortWasPublished) => {
    const { to, state } = getReturnLocation(returnLocation);
    navigate(to, {
      state: {
        ...state,
        returnFromCohortBuilder: true,
        cohortWasPublished,
        tenantCd,
        ...(cohortBuilderFlowId ? { cohortBuilderFlowId } : {}),
      },
    });
  }, [cohortBuilderFlowId, navigate, returnLocation, tenantCd]);

  const updateVizSize = useCallback(() => {
    const viz = vizRef.current;
    const container = containerRef.current;

    if (viz && container) {
      viz.width = container.clientWidth + 'px';
      viz.height = container.clientHeight + 'px';
    }
  }, []);

  useEffect(() => {
    if (!defaultUserState || hasRequestedUserContextRef.current) {
      return undefined;
    }

    hasRequestedUserContextRef.current = true;
    const timeoutId = setTimeout(() => {
      store.dispatch(fetchData(currentTenantCd, AppConfig.APP_CODE_FO, { explicitTenant: false }));
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [defaultUserState, currentTenantCd]);

  useEffect(() => {
    if (defaultUserState) {
      setLoading(true);
      setError(null);
      return undefined;
    }

    if (!tenantCd) {
      setLoading(false);
      setError('No tenant selected.');
      return;
    }
    if (!tableauServiceUrl) {
      setLoading(false);
      setError('Tableau service is not available. Please try again later.');
      return;
    }
    setLoading(true);
    setError(null);

    let timerId;
    let cancelled = false;

    // Build environment-aware authoring URL: {tbUrl}/trusted/tableauToken/t/{tenant}/authoring/...
    // getUrlTicket will replace 'tableauToken' with the actual trusted ticket
    const baseUrl = `${tableauServiceUrl}/trusted/tableauToken/t/${encodeURIComponent(tenantCd)}/${cohortAuthoringPath}`;

    getUrlTicket(baseUrl, tenantCd)
      .then((vizUrl) => {
        const container = containerRef.current;
        if (cancelled || !container) return;

        const viz = new TableauAuthoringViz();
        const querySeparator = vizUrl.includes('?') ? '&' : '?';
        viz.src = `${vizUrl}${querySeparator}:embed=yes`;
        viz.hideCloseButton = true;

        viz.addEventListener('firstinteractive', () => { clearTimeout(timerId); setLoading(false); }, { once: true });
        viz.addEventListener('workbookpublished', () => {
          try { viz.dispose(); } catch (e) { /* ignore */ }
          vizRef.current = null;
          returnToFrontOffice(true);
        }, { once: true });
        viz.addEventListener('workbookpublishedas', () => {
          try { viz.dispose(); } catch (e) { /* ignore */ }
          vizRef.current = null;
          returnToFrontOffice(true);
        }, { once: true });
        viz.addEventListener('vizerrorhandleevent', () => {
          clearTimeout(timerId);
          setLoading(false);
          setError('Failed to load Cohort Builder. Please try again.');
        }, { once: true });

        timerId = setTimeout(() => {
          setLoading(false);
          setError('Cohort Builder is taking too long to load. Please try again.');
        }, 15000);

        vizRef.current = viz;
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
        container.appendChild(viz);
        updateVizSize();
        window.addEventListener('resize', updateVizSize);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load Cohort Builder', err);
        setLoading(false);
        setError('Failed to load Cohort Builder. Please try again.');
      });

    return () => {
      cancelled = true;
      clearTimeout(timerId);
      window.removeEventListener('resize', updateVizSize);
      if (vizRef.current) {
        try { vizRef.current.dispose(); } catch (e) { /* ignore */ }
        vizRef.current = null;
      }
    };
  }, [cohortAuthoringPath, defaultUserState, tenantCd, tableauServiceUrl, returnToFrontOffice, updateVizSize]);

  const handleGoBack = useCallback(() => {
    if (vizRef.current) {
      try { vizRef.current.dispose(); } catch (e) { /* ignore */ }
      vizRef.current = null;
    }
    if (openedFromGlobalFilters) {
      returnToFrontOffice(false);
      return;
    }
    navigate(-1);
  }, [openedFromGlobalFilters, navigate, returnToFrontOffice]);

  useEffect(() => {
    const handleEscapeClose = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      handleGoBack();
    };

    window.addEventListener('keydown', handleEscapeClose);
    return () => window.removeEventListener('keydown', handleEscapeClose);
  }, [handleGoBack]);

  return (
    <Box sx={{ position: 'fixed', top: '80px', left: 0, right: 0, bottom: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1.5,
          borderTop: '1px solid #959696',
          borderBottom: '1px solid #E0E0E0',
          backgroundColor: '#FFFFFF',
          flexShrink: 0,
        }}>
        <IconButton
          onClick={handleGoBack}
          size="small"
          aria-label="Close cohort builder"
          sx={{
            color: '#2F54A5',
          }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Noto Sans',
            fontSize: 14,
            fontWeight: 600,
            color: '#333333',
          }}>
          Cohort Builder
        </Typography>
        {editCohortReptSource && editCohortDisplayText ? (
          <Typography
            sx={{
              fontFamily: 'Noto Sans',
              fontSize: 13,
              color: '#666666',
            }}>
            {`Editing "${editCohortDisplayText}"`}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'absolute', inset: 0, zIndex: 5, backgroundColor: '#FFFFFF' }}>
            <CircularProgress size={40} sx={{ color: '#2F54A5' }} />
          </Box>
        )}
        {error && (
          <Typography sx={{ p: 3, color: '#88041E', fontFamily: 'Noto Sans' }}>{error}</Typography>
        )}
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </Box>
    </Box>
  );
};

export default CohortBuilder;
